import defu from 'defu';
import { camelCase } from 'change-case';
import calculateSpecificity from 'css-specificity-calculator';
export default class StyleReader {
  constructor(breakpoints = {}, states = [], properties = { fontFamily: [] }) {
    this.breakpoints = breakpoints;
    this.states = states;
    this.properties = properties;
    this.stylesheets = [];
    this.allClasses = {};
    this.initialised = {};
    for (const property in this.properties) {
      this.initialiseProperty(this.initialised, property);
    }
  }
  getStyles(element, stylesheets) {
    const properties = JSON.parse(JSON.stringify(this.initialised));
    if (stylesheets !== this.stylesheets) {
      this.stylesheets = stylesheets;
      this.allClasses = this.getClasses(stylesheets, element.ownerDocument);
    }
    this.fitBreakpoints(this.allClasses);
    for (const breakpoint of ['all', ...Object.values(this.breakpoints)]) {
      let breakpointText = `(min-width: ${breakpoint})`;
      if (breakpoint === 'all') breakpointText = 'all';
      const appliedClasses = this.getAppliedClasses(
        element,
        this.allClasses[breakpointText] || []
      );
      const breakpointProperties = this.getPropertiesFromClasses(
        appliedClasses,
        element
      );
      properties[breakpointText] = defu(
        breakpointProperties,
        properties[breakpointText]
      );
    }
    this.setInlineStyles(element, properties);
    return properties;
  }
  getAllElementStyles(body, stylesheets) {
    const allClasses = this.getClasses(stylesheets, body.ownerDocument);
    this.fitBreakpoints(allClasses);
    let parserId = 0;
    body.querySelectorAll('*').forEach((node) => {
      node.setAttribute('data-gp-parser-id', parserId);
      parserId++;
    });
    const appliedClasses = {};
    for (const breakpoint of ['all', ...Object.values(this.breakpoints)]) {
      let breakpointText = `(min-width: ${breakpoint})`;
      if (breakpoint === 'all') breakpointText = 'all';
      if (!this.allClasses[breakpointText]) {
        this.allClasses[breakpointText] = [];
      }
      for (let i = 0; i < this.allClasses[breakpointText].length; i++) {
        if (!this.allClasses[breakpointText][i].matchesRefNode) {
          body
            .querySelectorAll(this.allClasses[breakpointText][i].selectorText)
            .forEach((node) => {
              const nodeParserId = node.getAttribute('data-gp-parser-id');
              if (!appliedClasses[nodeParserId]) {
                appliedClasses[nodeParserId] = {};
              }
              if (!appliedClasses[nodeParserId][breakpointText]) {
                appliedClasses[nodeParserId][breakpointText] = [];
              }
              appliedClasses[nodeParserId][breakpointText].push(
                this.allClasses[breakpointText][i]
              );
            });
        }
      }
    }
    return this.getAllElementProperties(body, appliedClasses);
  }
  getAllElementProperties(body, appliedClasses) {
    const stylemaps = [];
    for (const id in appliedClasses) {
      const element = body.querySelector(`[data-gp-parser-id="${id}"]`);
      const properties = {};
      for (const breakpoint of ['all', ...Object.values(this.breakpoints)]) {
        let breakpointText = `(min-width: ${breakpoint})`;
        if (breakpoint === 'all') breakpointText = 'all';
        if (!appliedClasses[id][breakpointText]) continue;
        const breakpointProperties = this.getPropertiesFromClasses(
          appliedClasses[id][breakpointText],
          element
        );
        properties[breakpointText] = breakpointProperties;
      }
      this.setInlineStyles(element, properties);
      stylemaps.push({ element, properties });
    }
    body.querySelectorAll(`[data-gp-parser-id]`).forEach((node) => {
      node.removeAttribute('data-gp-parser-id');
    });
    return stylemaps;
  }
  fitBreakpoints(classes) {
    const builderBreakpoints = [...Object.values(this.breakpoints)]
      .map((breakpoint) => parseInt(breakpoint.replace(/\D/g, '')))
      .sort((a, b) => {
        return a - b;
      });
    const contentBreakpoints = Object.keys(classes)
      .map((breakpoint) => ({
        text: breakpoint,
        px:
          !breakpoint.includes('max') &&
          breakpoint.split('px')[0] &&
          parseInt(breakpoint.split('px')[0].replace(/\D/g, '')),
      }))
      .filter((breakpoint) => breakpoint.px)
      .sort((a, b) => {
        return a.px - b.px;
      });
    contentBreakpoints.forEach((breakpoint) => {
      if (!builderBreakpoints.length) return;
      const closest = builderBreakpoints.reduce((prev, curr) => {
        return Math.abs(curr - breakpoint.px) < Math.abs(prev - breakpoint.px)
          ? curr
          : prev;
      });
      if (closest !== breakpoint.px) {
        classes[`(min-width: ${closest}px)`] = [
          ...(classes[`(min-width: ${closest}px)`] || []),
          ...classes[breakpoint.text],
        ];
      }
    });
  }
  setInlineStyles(element, properties) {
    [...element.style].forEach((style) => {
      for (const breakpoint in properties) {
        properties[breakpoint]['none'][camelCase(style)] = element.style[style];
      }
    });
  }
  initialiseProperty(values, property) {
    for (const breakpoint of ['all', ...Object.values(this.breakpoints)]) {
      let breakpointText = `(min-width: ${breakpoint})`;
      if (breakpoint === 'all') breakpointText = 'all';
      if (!values[breakpointText]) values[breakpointText] = {};
      for (const variant of ['none', ...this.states]) {
        if (!values[breakpointText][variant])
          values[breakpointText][variant] = {};
        values[breakpointText][variant][property] = null;
      }
    }
  }
  getClasses(stylesheets, matchDocument) {
    const classes = {
      all: [],
    };
    stylesheets.forEach((sheet) =>
      [...(sheet.cssRules || sheet.rules || [])].forEach((rule) => {
        if (rule.selectorText) classes.all.push(rule);
        else if (rule.conditionText) {
          classes[rule.conditionText] = classes[rule.conditionText] || [];
          classes[rule.conditionText] = [
            ...classes[rule.conditionText],
            ...rule.cssRules,
          ];
        }
      })
    );
    const refNode = matchDocument.createElement('custom-refnode');
    for (const breakpoint in classes) {
      classes[breakpoint] = classes[breakpoint]
        .map((classData) => {
          let selectorText = classData.selectorText || '';
          this.states.forEach((variant) => {
            selectorText = selectorText.replace(new RegExp(`:${variant}$`), '');
          });
          classData.parsedSelectorText = selectorText;
          if (selectorText && refNode.matches(selectorText)) {
            classData.matchesRefNode = true;
          }
          return classData;
        })
        .filter((classData) => classData.parsedSelectorText);
    }
    return classes;
  }
  getClassnameFromSelector(selector) {
    return selector.replace('.', '').replace(new RegExp('\\\\', 'g'), '');
  }
  getAppliedClasses(el, classes) {
    const appliedClasses = [];
    for (let i = 0; i < classes.length; i++) {
      if (
        (!classes[i].matchesRefNode ||
          el.tagName === 'BODY' ||
          el.tagName === 'BASE-REFNODE') &&
        el.matches(classes[i].parsedSelectorText)
      ) {
        appliedClasses.push(classes[i]);
      }
    }
    return appliedClasses;
  }
  getPropertiesFromClasses(classes, element) {
    const properties = {};
    classes.forEach((classData) => {
      const cssText = classData.style.cssText.split(';');
      let specicifity = this.getSpecicififty(classData.selectorText, element);
      let stateVariant = 'none';
      this.states.forEach((variant) => {
        if (classData.cssText.includes(`:${variant}`)) stateVariant = variant;
      });
      if (!properties[stateVariant]) {
        properties[stateVariant] = {};
      }
      for (let i = classData.style.length; i--; ) {
        const nameString = camelCase(classData.style[i]);
        if (!properties[stateVariant][nameString]) {
          properties[stateVariant][nameString] = [];
        }
        let propertySpecicifity = specicifity;
        let propertyString = classData.style[nameString];
        if (
          cssText.find(
            (string) =>
              string
                .trim()
                .substr(0, classData.style[i].split('-')[0].trim().length) ===
                classData.style[i].split('-')[0].trim() &&
              string.includes('!important')
          )
        ) {
          propertySpecicifity = '10000';
          propertyString += ' !important';
        }
        properties[stateVariant][nameString].push({
          style: propertyString,
          specicifity: propertySpecicifity,
        });
      }
    });
    for (const property in properties) {
      for (const variant in properties[property]) {
        properties[property][variant] = properties[property][variant].reduce(
          (max, current) => {
            if (max.specicifity > current.specicifity) {
              return max;
            }
            return current;
          }
        ).style;
      }
    }
    return properties;
  }
  getSpecicififty(selectorText, element) {
    const selectors = selectorText.split(',');
    const matching = [];
    selectors.forEach((selector) => {
      if (selector.trim() && element.matches(selector)) {
        matching.push(selector);
      }
    });
    let specicifity = '';
    matching.forEach((selector) => {
      const selectorSpecicifity = calculateSpecificity(selector.trim()).join(
        ''
      );
      if (selectorSpecicifity > specicifity) {
        specicifity = selectorSpecicifity;
      }
    });
    return specicifity;
  }
}
